import { clsx } from "clsx";
import { FC, useMemo } from "react";

import { PopOver, SegmentedControl } from "@tsmono/react/components";
import { Icon } from "@tsmono/react/icons";

import { getFieldKey, type PickerColumn } from "../shared/gridUtils";

import styles from "./ColumnSelectorPopover.module.css";

export type ColumnScoresViewMode = "by-metric" | "per-scorer";

const isColumnScoresViewMode = (value: string): value is ColumnScoresViewMode =>
  value === "by-metric" || value === "per-scorer";

interface ColumnSelectorPopoverProps {
  showing: boolean;
  setShowing: (showing: boolean) => void;
  columns: PickerColumn[];
  /** Optional explicit visibility map. When provided, the popover reads
   *  current state from here rather than from each column's `hide`. Use
   *  this when the grid applies visibility via `applyColumnState`
   *  rather than via the column def. */
  visibility?: Record<string, boolean>;
  onVisibilityChange: (visibility: Record<string, boolean>) => void;
  positionEl: HTMLElement | null;
  filteredFields?: string[];
  scoresHeading?: string;
  /** Split columns into "Base" and a separate scores section. Default
   *  `true`. Set `false` for callers that don't need the split — both
   *  sections render under a single unified list instead. */
  splitScores?: boolean;
  /**
   * When true, renders a "By Metric" / "Per Scorer" segmented control in the
   * scores section header. The caller controls which view is active and owns
   * persistence; this component just dispatches the change.
   */
  groupableScores?: boolean;
  scoresViewMode?: ColumnScoresViewMode;
  onScoresViewModeChange?: (mode: ColumnScoresViewMode) => void;
  /** When provided, renders a "Default" link next to All / None in the
   *  Base section that resets the column list (order + visibility) to
   *  the caller's notion of the default. */
  onResetToDefault?: () => void;
}

// Fields that belong in the scores section. Covers both per-scorer
// `score_<scorer>/<metric>` fields and the synthetic by-metric
// `metric_<metricName>` fields emitted by useLogListColumns.
const isScoreField = (field: string): boolean =>
  field.startsWith("score_") || field.startsWith("metric_");

export const ColumnSelectorPopover: FC<ColumnSelectorPopoverProps> = ({
  showing,
  setShowing,
  columns,
  onVisibilityChange,
  positionEl,
  filteredFields = [],
  scoresHeading = "Scorers",
  splitScores = true,
  visibility,
  groupableScores = false,
  scoresViewMode = "by-metric",
  onScoresViewModeChange,
  onResetToDefault,
}) => {
  // Read current visibility from the explicit prop when supplied,
  // otherwise fall back to each column's `hide`.
  const currentVisibility = useMemo(
    () =>
      columns.reduce<Record<string, boolean>>((acc, col) => {
        const key = getFieldKey(col);
        const vis = visibility ? visibility[key] !== false : !col.hide;
        return { ...acc, [key]: vis };
      }, {}),
    [columns, visibility]
  );

  const handleToggle = (field: string) => {
    onVisibilityChange({
      ...currentVisibility,
      [field]: !currentVisibility[field],
    });
  };

  // Group columns by category - merge optional into base for this dialog.
  // When `splitScores` is false, all columns are shown as a single list.
  const columnGroups = useMemo(() => {
    if (!splitScores) return { base: columns, scores: [] };
    return {
      base: columns.filter((col) => !isScoreField(getFieldKey(col))),
      scores: columns.filter((col) => isScoreField(getFieldKey(col))),
    };
  }, [columns, splitScores]);

  const handleSelectAllBase = () => {
    onVisibilityChange({
      ...currentVisibility,
      ...Object.fromEntries(
        columnGroups.base.map((col) => [getFieldKey(col), true])
      ),
    });
  };
  const handleDeselectAllBase = () => {
    onVisibilityChange({
      ...currentVisibility,
      ...Object.fromEntries(
        columnGroups.base.map((col) => [getFieldKey(col), false])
      ),
    });
  };
  const handleSelectAllScores = () => {
    onVisibilityChange({
      ...currentVisibility,
      ...Object.fromEntries(
        columnGroups.scores.map((col) => [getFieldKey(col), true])
      ),
    });
  };
  const handleDeselectAllScores = () => {
    onVisibilityChange({
      ...currentVisibility,
      ...Object.fromEntries(
        columnGroups.scores.map((col) => [getFieldKey(col), false])
      ),
    });
  };

  const renderColumnCheckbox = (col: PickerColumn) => {
    const field = getFieldKey(col);
    const hasFilter = filteredFields.includes(field);
    return (
      <div
        key={field}
        className={styles.checkboxWrapper}
        title={
          hasFilter
            ? "Unselecting will remove an active filter on this column"
            : undefined
        }
      >
        <label className={styles.label}>
          <input
            type="checkbox"
            checked={currentVisibility[field]}
            onChange={() => handleToggle(field)}
            className={styles.checkbox}
          />
          <span>{col.headerName || field}</span>
          {hasFilter && <Icon name="filter" className={styles.filterIcon} />}
        </label>
      </div>
    );
  };

  return (
    <PopOver
      id="column-selector-popover"
      isOpen={showing}
      setIsOpen={setShowing}
      positionEl={positionEl}
      placement="bottom-end"
      showArrow={false}
      hoverDelay={-1}
      closeOnMouseLeave={false}
      offset={[0, 1]}
    >
      <div className={clsx(styles.scrollableContainer, "text-size-small")}>
        <div className={clsx(styles.section)}>
          <div className={styles.headerRow}>
            {splitScores ? <b>Base</b> : <b>Columns</b>}
            <div className={clsx(styles.buttonContainer, "text-size-small")}>
              <button
                type="button"
                className={clsx(styles.button, "text-size-small")}
                onClick={handleSelectAllBase}
              >
                All
              </button>
              |
              <button
                type="button"
                className={clsx(styles.button)}
                onClick={handleDeselectAllBase}
              >
                None
              </button>
              {onResetToDefault && (
                <>
                  |
                  <button
                    type="button"
                    className={clsx(styles.button)}
                    onClick={onResetToDefault}
                  >
                    Default
                  </button>
                </>
              )}
            </div>
          </div>
          <div className={styles.columnsLayout}>
            {columnGroups.base.map((col) => renderColumnCheckbox(col))}
          </div>
        </div>

        {splitScores && columnGroups.scores.length > 0 && (
          <div>
            <div className={styles.headerRow}>
              <div className={styles.scoresHeadingGroup}>
                <b>{scoresHeading}</b>
                {groupableScores && onScoresViewModeChange && (
                  <div className={styles.scoresViewModeControl}>
                    <SegmentedControl
                      id="column-selector-scores-view-mode"
                      segments={[
                        { id: "by-metric", label: "By Metric" },
                        { id: "per-scorer", label: "Per Scorer" },
                      ]}
                      selectedId={scoresViewMode}
                      onSegmentChange={(id) =>
                        isColumnScoresViewMode(id) && onScoresViewModeChange(id)
                      }
                    />
                  </div>
                )}
              </div>
              <div className={styles.buttonContainer}>
                <button
                  type="button"
                  className={clsx(styles.button)}
                  onClick={handleSelectAllScores}
                >
                  All
                </button>
                |
                <button
                  type="button"
                  className={clsx(styles.button)}
                  onClick={handleDeselectAllScores}
                >
                  None
                </button>
              </div>
            </div>
            <div className={styles.columnsLayout}>
              {columnGroups.scores.map((col) => renderColumnCheckbox(col))}
            </div>
          </div>
        )}
      </div>
    </PopOver>
  );
};
